import React, { useEffect, useRef, useState, useMemo, useCallback } from "react"
import { useInterval } from "react-use"
import ScheduleTable from "scripts/components/react/ui/ScheduleTable"
import Schedule from "./Schedule"

// scrollIntoViewIfNeeded polyfill
if (!Element.prototype.scrollIntoViewIfNeeded) {
  Element.prototype.scrollIntoViewIfNeeded = function scrollInoViewIfNeededPolyfill(
    centerIfNeeded,
  ) {
    function makeRange(start, length) {
      return { start, length, end: start + length }
    }

    function coverRange(inner, outer) {
      if (
        centerIfNeeded === false ||
        (outer.start < inner.end && inner.start < outer.end)
      ) {
        return Math.min(inner.start, Math.max(outer.start, inner.end - outer.length))
      }
      return (inner.start + inner.end - outer.length) / 2
    }

    function makePoint(x, y) {
      return {
        x,
        y,
        translate: function translate(dX, dY) {
          return makePoint(x + dX, y + dY)
        },
      }
    }

    function absolute(elem, pt) {
      while (elem) {
        pt = pt.translate(elem.offsetLeft, elem.offsetTop)
        elem = elem.offsetParent
      }
      return pt
    }

    let target = absolute(this, makePoint(0, 0))
    const extent = makePoint(this.offsetWidth, this.offsetHeight)
    let elem = this.parentNode
    let origin

    while (elem instanceof HTMLElement) {
      // Apply desired scroll amount.
      origin = absolute(elem, makePoint(elem.clientLeft, elem.clientTop))
      elem.scrollLeft = coverRange(
        makeRange(target.x - origin.x, extent.x),
        makeRange(elem.scrollLeft, elem.clientWidth),
      )
      elem.scrollTop = coverRange(
        makeRange(target.y - origin.y, extent.y),
        makeRange(elem.scrollTop, elem.clientHeight),
      )

      // Determine actual scroll amount by reading back scroll properties.
      target = target.translate(-elem.scrollLeft, -elem.scrollTop)
      elem = elem.parentNode
    }
  }
}

function Item({ listIndex, store, isSelected }) {
  const { phrases } = window
  const { identifier, address, schedule } = store

  const [isOpenSchedule, setIsOpenSchedule] = useState(false)
  const [storeSchedule, setStoreSchedule] = useState(() => new Schedule(schedule))
  const listItemRef = useRef(null)
  const storeStatusEl = useRef(null)

  const statusLabel = useMemo(() => {
    // return `<span className="store__status--primary">Открыто</span> сегодня до ${storeSchedule.todaySchedule[1]}`
    if (storeSchedule.isOpen)
      return (
        phrases.shops.open_at.replace(":time", storeSchedule.todaySchedule[1]) ||
        `<span className="store__status--primary">Open</span> today until ${storeSchedule.todaySchedule[1]}`
      )
    // return `сьогодні до ${storeSchedule.todaySchedule[1]}`;
    // if (storeSchedule.isNextWorkingDayTomorrow) {
    //   return phrases.shops.open_at.replace(':time', storeSchedule.todaySchedule[1]) || `<span className="store__status--primary">Открыто</span> сегодня до ${storeSchedule.todaySchedule[1]}`
    //   // return `Відчиниться завтра о ${storeSchedule.nextWorkingDaySchedule[0]}`;
    // }
    if (storeSchedule.nextWorkingDay === storeSchedule.todayName) {
      return (
        phrases.shops.closed_at.replace(
          ":time",
          storeSchedule.nextWorkingDaySchedule[0],
        ) ||
        `<span class="store__status--error">Closed</span> open at ${storeSchedule.nextWorkingDaySchedule[0]}`
      )
      // return `Відчиниться сьогодні о ${storeSchedule.nextWorkingDaySchedule[0]}`;
    }
    return (
      `${phrases.shops.closed_at_no_time} ${
        phrases?.shops?.days?.[storeSchedule.nextWorkingDay]
      } ${storeSchedule.nextWorkingDaySchedule[0]}` ||
      `<span class="store__status--error">Closed</span> open at`
    )
    // return `Відчиниться в ${storeSchedule.nextWorkingDay} о ${storeSchedule.nextWorkingDaySchedule[0]}`;
  }, [
    storeSchedule.isOpen,
    storeSchedule.todaySchedule,
    storeSchedule.nextWorkingDay,
    storeSchedule.todayName,
    storeSchedule.nextWorkingDaySchedule,
    phrases.shops,
  ])

  const className = useMemo(() => {
    const isSelectedClass = isSelected ? "store-card--selected" : ""
    const isOpenScheduleClass = isOpenSchedule && isSelected ? "is-open-schedule" : ""
    const isOpenClass = storeSchedule.isOpen ? "" : "closed"
    return `store-card store-card__content store-card__content--column ${isSelectedClass} ${isOpenScheduleClass} ${isOpenClass}`
  }, [isSelected, isOpenSchedule, storeSchedule.isOpen])

  const handleToggleSchedule = useCallback(() => {
    setIsOpenSchedule((state) => !state)
  }, [])

  useInterval(() => {
    setStoreSchedule(new Schedule(schedule))
  }, 300000)

  useEffect(() => {
    if (storeStatusEl.current) {
      storeStatusEl.current.innerHTML = `${statusLabel} <button class="js-accordion__trigger">
      <svg class="icon-arrow">
        <use xlink:href="#arrow" />
      </svg>
    </button>`
    }
  }, [storeStatusEl, storeSchedule.isOpen, statusLabel])

  useEffect(() => {
    if (!isSelected) setIsOpenSchedule(false)
    if (isSelected && listItemRef.current) {
      // Defer scroll to not block INP
      const scheduleScroll =
        typeof window.requestIdleCallback === "function"
          ? window.requestIdleCallback
          : (cb) => setTimeout(cb, 0)

      scheduleScroll(() => {
        listItemRef.current?.scrollIntoViewIfNeeded({
          behavior: "smooth",
          block: "start",
        })
      })
    }
  }, [isSelected])

  return (
    <li
      ref={listItemRef}
      role="option"
      aria-selected={isSelected}
      tabIndex={listIndex}
      className={className}
      data-store-id={identifier}
    >
      <div className="store-card__data">
        <div className="title title--md">{address}</div>
        <span
          className="store-card__status store-card__status--expandable"
          onClick={handleToggleSchedule}
          ref={storeStatusEl}
        />
      </div>
      <div className="store-card__schedule js_store_schedule js-accordion__content">
        <ScheduleTable
          storeSchedule={storeSchedule}
          className="store-card__schedule-table"
          phrases={phrases.shops}
        />
      </div>
    </li>
  )
}

// Custom comparison - only re-render if selection state or store data changes
const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.store.identifier === nextProps.store.identifier &&
    prevProps.store.address === nextProps.store.address &&
    prevProps.listIndex === nextProps.listIndex
  )
}

export default React.memo(Item, areEqual)
